import React from "react";
import { AlertTriangle, Clock, FileWarning, HelpCircle, CircleDot } from "lucide-react";

export function Badge({ children, tone = "muted", className = "", ...props }) {
  const tones = {
    muted: "bg-white/5 text-muted-foreground border-white/10",
    primary: "bg-primary/12 text-primary border-primary/25",
    success: "bg-[#6B8E23]/12 text-[#9DBE5C] border-[#6B8E23]/30",
    warning: "bg-[#D97706]/12 text-[#F0A94A] border-[#D97706]/30",
    danger: "bg-[#B91C1C]/12 text-[#F08A8A] border-[#B91C1C]/30",
    slate: "bg-secondary text-secondary-foreground border-white/10",
  };
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[11px] font-medium ${tones[tone]} ${className}`} {...props}>
      {children}
    </span>
  );
}

export function Dot({ color }) {
  return <span className="inline-block w-1.5 h-1.5 rounded-full shrink-0" style={{ background: color }} />;
}

export function SectionHeader({ title, sub, right }) {
  return (
    <div className="flex items-end justify-between mb-4">
      <div>
        <h3 className="font-display font-semibold text-[15px] tracking-tight text-foreground">{title}</h3>
        {sub && <p className="text-[12px] text-muted-foreground mt-0.5">{sub}</p>}
      </div>
      {right}
    </div>
  );
}

export function Card({ children, className = "", ...props }) {
  return (
    <div className={`surface hairline rounded-lg ${className}`} {...props}>
      {children}
    </div>
  );
}

export function EmptyState({ icon: Icon, title, sub, action }) {
  return (
    <div className="flex flex-col items-center justify-center text-center py-16 px-6">
      {Icon && <Icon size={28} className="text-muted-foreground/50 mb-3" />}
      <div className="text-sm text-foreground">{title}</div>
      {sub && <div className="text-[12px] text-muted-foreground mt-1 max-w-sm">{sub}</div>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

const sevIcon = { high: AlertTriangle, warning: FileWarning, info: HelpCircle, deadline: Clock };
const sevColor = { high: "#F08A8A", warning: "#F0A94A", info: "#7fa8cc" };

export function AttentionItem({ item, onClick }) {
  const Icon = item.type === "deadline" ? Clock : (sevIcon[item.severity] || CircleDot);
  return (
    <button
      onClick={onClick}
      data-testid="attention-item"
      className="w-full flex items-start gap-2.5 py-2.5 px-3 rounded-md text-left hover:bg-white/[0.03] lift border border-transparent hover:border-white/8"
    >
      <Icon size={15} style={{ color: sevColor[item.severity] || "#8b8b8b" }} className="mt-0.5 shrink-0" />
      <span className="text-[13px] text-foreground/90 leading-snug">{item.text}</span>
    </button>
  );
}

export function StrengthPill({ strength }) {
  const map = {
    strong: { tone: "success", label: "Strong" },
    moderate: { tone: "primary", label: "Moderate" },
    weak: { tone: "danger", label: "Weak" },
    unknown: { tone: "muted", label: "Unknown" },
  };
  const m = map[strength] || map.unknown;
  return <Badge tone={m.tone}>{m.label}</Badge>;
}

export function UsageMeter({ used, allowed, limitType }) {
  if (limitType === "unlimited") {
    return <span className="text-[11px] text-muted-foreground font-mono-legal">unlimited</span>;
  }
  const pct = allowed ? Math.min(100, (used / allowed) * 100) : 0;
  const full = allowed && used >= allowed;
  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-1">
        <span className="text-[11px] font-mono-legal text-muted-foreground">
          {used} of {allowed} used
        </span>
        {full && <span className="text-[10px] text-[#F08A8A] uppercase tracking-wide">at cap</span>}
      </div>
      <div className="h-1.5 rounded-full bg-white/8 overflow-hidden">
        <div
          className="h-full rounded-full transition-[width] duration-500"
          style={{ width: `${pct}%`, background: full ? "#B91C1C" : "var(--bronze)" }}
        />
      </div>
    </div>
  );
}
